import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PW } from "@majourneys/booking";
import { Icon } from "@majourneys/icons";
import { panelStyle } from "../../../shared/ui/card-frame";
import { SignInForm } from "./sign-in-form";

/** The /sign-in page's background, so the dialog reads as that page. */
const PAGE_BG = "linear-gradient(180deg, #F1F2F9 0%, #F6F7FB 40%, #FAFAFC 100%)";

interface SignInModalProps {
  open: boolean;
  /** Called whenever the dialog goes away, signed in or not. */
  onClose: () => void;
  /** Called after a sign-in completes here, before `onClose`. */
  onSignedIn?: () => void;
  /** Called when the guest closes it without signing in (close, Escape, backdrop). */
  onDismiss?: () => void;
  /** Why the dialog opened, when a host has one (the heart: "Save it for later"). */
  heading?: string;
  subheading?: string;
  lead?: ReactNode;
}

/**
 * Sign-in dialog: opens over the current page instead of navigating to
 * /sign-in, and keeps the visitor where they are after a successful email
 * sign-in (the better-auth session store refreshes the header on its own).
 * Social OAuth redirects back to the current path.
 *
 * The dialog is a full-screen layer in the /sign-in page's own background, so
 * opening it looks like arriving on /sign-in without leaving. The form sits in
 * a pewter card on that background (owner, 2026-10-11, which replaces the
 * 2026-10-05 "no card" rule for this popup); controls inside stay white with
 * one hairline.
 */
export function SignInModal({ open, onClose, onSignedIn, onDismiss, heading, subheading, lead }: SignInModalProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const dismiss = () => {
    onDismiss?.();
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onDismiss?.();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, onClose, onDismiss]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-modal)] overflow-y-auto"
      style={{ background: PAGE_BG }}
      role="dialog"
      aria-modal="true"
      aria-label={heading ?? t("auth.one_field.title")}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("common.close")}
        className="fixed end-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full transition-colors hover:bg-[rgba(17,14,57,0.06)]"
        style={{ color: PW.navy }}
      >
        <Icon name="ri-close-line" className="text-[22px]" aria-hidden />
      </button>

      <div className="flex min-h-full items-start justify-center px-4 pb-10 pt-[72px] sm:items-center sm:pt-10">
        <div className="w-full min-w-0 max-w-[440px] px-5 py-6 sm:px-7 sm:py-7" style={panelStyle(28)}>
          <SignInForm
            redirectPath={location.href}
            onSignedIn={() => {
              onSignedIn?.();
              onClose();
            }}
            heading={heading}
            subheading={subheading}
            lead={lead}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
